// Cave84 — Critic Scores service (Phase 2 Week 7)
// Subcollection: vintages/{vintageId}/critic_scores/{scoreId}
// Read-only from the UI; written by the Claude Code batch (generate-analyses).
// Owner can add scores manually via the UI (manual_entry source).

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where
} from 'firebase/firestore';
import type { CriticName, CriticScore } from '@cave84/shared-types';
import { db } from './firebase';

// ---------------------------------------------------------------------------
// NOTE: Critic scores are stored in the ROOT collection `critic_scores/{id}`,
// not as a subcollection of vintages. The import-cellartracker.ts script writes
// to `critic_scores/`, so all queries filter by `vintage_id` field.
// ---------------------------------------------------------------------------

function scoresRef() {
  return collection(db, 'critic_scores');
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listCriticScoresByVintage(vintageId: string): Promise<CriticScore[]> {
  const snap = await getDocs(
    query(scoresRef(), where('vintage_id', '==', vintageId))
  );
  // Sort client-side — avoids composite index requirement
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() } as CriticScore))
    .sort((a, b) => b.score_value - a.score_value);
}

/** Fetch only is_top_3=true scores — for the prominent display on WineAnalysisPage. */
export async function listTop3ScoresByVintage(vintageId: string): Promise<CriticScore[]> {
  const snap = await getDocs(
    query(scoresRef(), where('vintage_id', '==', vintageId))
  );
  // Filter + sort client-side — avoids composite index requirement
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() } as CriticScore))
    .filter(s => s.is_top_3)
    .sort((a, b) => b.score_value - a.score_value);
}

// ---------------------------------------------------------------------------
// Manual entry (owner only)
// ---------------------------------------------------------------------------

export type CriticScoreCreateInput = Omit<CriticScore, 'id' | 'created_at'>;

export async function addCriticScore(
  vintageId: string,
  input: CriticScoreCreateInput
): Promise<string> {
  const ref = await addDoc(scoresRef(), {
    ...input,
    vintage_id: vintageId,
    created_at: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteCriticScore(scoreId: string): Promise<void> {
  await deleteDoc(doc(db, 'critic_scores', scoreId));
}

// ---------------------------------------------------------------------------
// Helpers — critic display names and score formatting
// ---------------------------------------------------------------------------

export const CRITIC_DISPLAY: Record<CriticName, { label: string; abbr: string }> = {
  wine_advocate:             { label: 'Wine Advocate',            abbr: 'WA'  },
  vinous:                    { label: 'Vinous',                   abbr: 'Vin' },
  burghound:                 { label: 'Burghound',                abbr: 'BH'  },
  decanter:                  { label: 'Decanter',                 abbr: 'Dec' },
  jancis_robinson:           { label: 'Jancis Robinson',          abbr: 'JR'  },
  wine_spectator:            { label: 'Wine Spectator',           abbr: 'WS'  },
  james_suckling:            { label: 'James Suckling',           abbr: 'JS'  },
  jeb_dunnuck:               { label: 'Jeb Dunnuck',              abbr: 'JD'  },
  monica_larner:             { label: 'Monica Larner',            abbr: 'ML'  },
  la_revue_du_vin_de_france: { label: 'La Revue du Vin de France',abbr: 'RVF' },
  bettane_desseauve:         { label: 'Bettane & Desseauve',      abbr: 'B&D' },
  william_kelley:            { label: 'William Kelley',           abbr: 'WK'  },
  other:                     { label: 'Other',                    abbr: '--'  },
};

export function formatScore(score: CriticScore): string {
  if (score.score_range) return score.score_range;
  switch (score.score_type) {
    case '100_pt': return score.score_value.toString();
    case '20_pt':  return score.score_value + '/20';
    case '5_star': return '*'.repeat(Math.round(score.score_value));
    default:       return score.score_value.toString();
  }
}
